import { z } from 'zod'
import { AD_PERIOD_DAYS } from '../helpers/ad-periods.js'

/**
 * How many weeks a pipeline shows at once (docs/pipeline-view.spec.md, "Spans"):
 * whole two-week periods, ending with the period asked for. Two weeks is a
 * handful of couples at one venue; twelve weeks is enough to tell a change from
 * chance.
 */
export const PIPELINE_SPANS = ['two_weeks', 'six_weeks', 'twelve_weeks', 'twelve_months'] as const

export const pipelineSpanSchema = z.enum(PIPELINE_SPANS)
export type PipelineSpan = z.infer<typeof pipelineSpanSchema>

/** How many two-week periods each span holds. Twelve months is 26 periods: 364 days, so weekdays line up a year apart. */
export const PIPELINE_SPAN_PERIODS: Readonly<Record<PipelineSpan, number>> = {
  two_weeks: 1,
  six_weeks: 3,
  twelve_weeks: 6,
  twelve_months: 26,
}

/** A year in whole periods: 364 days, so a Sunday a year back is a Sunday. What "last year" and "the year before" mean. */
export const PIPELINE_YEAR_DAYS = PIPELINE_SPAN_PERIODS.twelve_months * AD_PERIOD_DAYS

export const PIPELINE_SPAN_LABELS: Readonly<Record<PipelineSpan, string>> = {
  two_weeks: '2 weeks',
  six_weeks: '6 weeks',
  twelve_weeks: '12 weeks',
  twelve_months: '12 months',
}

/**
 * What a span is compared with: the same weeks a year before (`last_year`,
 * 364 days back, so a wedding season is set against a wedding season), or the
 * weeks just before it (`before`).
 */
export const PIPELINE_COMPARISONS = ['last_year', 'before'] as const

export const pipelineComparisonSchema = z.enum(PIPELINE_COMPARISONS)
export type PipelineComparison = z.infer<typeof pipelineComparisonSchema>
